import { AlertTriangle, Check } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { GoalBadge } from "@/components/goal-badge";
import { RubricTable } from "@/components/rubric-summary";
import { Spinner } from "@/components/data-state";
import type { RubricCheck, SimulatorSide } from "@/lib/api";
import { ApiError, adminApi } from "@/lib/api";
import { formatPoints } from "@/lib/scoring";
import { cn } from "@/lib/utils";

/**
 * The rubric editor: the text on the left, what it means on the right.
 *
 * A textarea, because the rubric *is* text -- three sentences of semantics a TA can hold in
 * their head, written in a language whose reference fits under the box. What the editor
 * adds is the server's reading of it as they type: the parsed blocks and the maximum, the
 * line and reason when it does not parse, and five sample runs scored under the draft so
 * the consequences of a rule are visible before any student meets it. The same parser and
 * evaluator grade the samples and the students, so the preview cannot lie.
 */
export function RubricEditor({
  id,
  value,
  onChange,
  simulator,
  adminKey,
  onChecked,
  aside = "right",
  className,
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
  simulator: SimulatorSide;
  adminKey: string;
  /** Told the server's reading of the current text, for the form around it to gate on. */
  onChecked?: (check: RubricCheck | null) => void;
  /** Where the server's reading goes: beside the text (a page) or under it (a dialog). */
  aside?: "right" | "below";
  className?: string;
}) {
  const [check, setCheck] = useState<RubricCheck | null>(null);
  // Which text the current `check` answers; a mismatch is "checking".
  const [checkedText, setCheckedText] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const latest = useRef(0);
  const pending = checkedText !== value;

  useEffect(() => {
    const seq = ++latest.current;
    const timer = setTimeout(async () => {
      try {
        const result = await adminApi.checkRubric(value, simulator, adminKey);
        if (seq !== latest.current) return;
        setCheck(result);
        setFailure(null);
        setCheckedText(value);
        onChecked?.(result);
      } catch (caught) {
        if (seq !== latest.current) return;
        setFailure(caught instanceof ApiError ? caught.message : "Could not check the rubric");
        setCheckedText(value);
        onChecked?.(null);
      }
    }, 300);
    return () => clearTimeout(timer);
    // onChecked is a callback prop; re-running on its identity would loop a parent that
    // passes an inline arrow. The text, mode and key are what decide the answer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, simulator, adminKey]);

  const problem = check && !check.ok ? check.problem : null;

  return (
    <div
      className={cn(
        "grid gap-3",
        aside === "right" && "lg:grid-cols-[minmax(0,1fr)_20rem]",
        className,
      )}
    >
      <div className="grid min-w-0 gap-2">
        <div className="flex items-baseline justify-between gap-3">
          <label htmlFor={id} className="text-sm font-medium">
            Scoring rules
          </label>
          <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
            {pending ? (
              <>
                <Spinner className="size-3" /> checking
              </>
            ) : failure ? (
              <span className="text-destructive">{failure}</span>
            ) : problem ? (
              <span className="text-destructive flex items-center gap-1">
                <AlertTriangle className="size-3.5" aria-hidden />
                line {problem.line}: {problem.message}
              </span>
            ) : check?.spec ? (
              <span className="flex items-center gap-1 text-emerald-700 dark:text-emerald-300">
                <Check className="size-3.5" aria-hidden />
                {formatPoints(check.spec.max_points)} points possible
              </span>
            ) : null}
          </span>
        </div>
        <textarea
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={Math.max(10, value.split("\n").length + 1)}
          spellCheck={false}
          aria-invalid={problem ? true : undefined}
          className={cn(
            "border-input bg-transparent focus-visible:border-ring focus-visible:ring-ring/50 w-full min-w-0 resize-y rounded-md border px-3 py-2 font-mono text-sm leading-6 outline-none focus-visible:ring-3",
            problem && "border-destructive/60",
          )}
        />
        <Reference simulator={simulator} />
      </div>

      <aside className={cn("grid min-w-0 content-start gap-3", aside === "below" && "sm:grid-cols-2")}>
        <div className="min-w-0 rounded-md border p-3">
          <p className="text-muted-foreground mb-2 text-xs font-medium">As the server reads it</p>
          {check?.spec ? (
            <RubricTable spec={check.spec} />
          ) : (
            <p className="text-muted-foreground text-xs">
              {problem ? "Fix the line above to see the rules." : "…"}
            </p>
          )}
        </div>
        {check?.ok && check.preview.length ? (
          <div className="min-w-0 rounded-md border p-3">
            <p className="text-muted-foreground mb-2 text-xs font-medium">Sample runs under these rules</p>
            <ul className="grid gap-1.5 text-xs">
              {check.preview.map((row) => (
                <li key={row.name} className="flex min-w-0 items-center justify-between gap-2">
                  <span className="min-w-0 flex-1 truncate" title={describe(row.metrics)}>
                    {row.name}
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <GoalBadge tier={row.tier} className="text-[10px]" />
                    <span className="font-mono tabular-nums">
                      <span className="font-semibold">{formatPoints(row.score.points)}</span>
                      <span className="text-muted-foreground">/{formatPoints(row.score.max_points)}</span>
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </aside>
    </div>
  );
}

function describe(metrics: Record<string, number | null>): string {
  return Object.entries(metrics)
    .filter(([, v]) => v !== null)
    .map(([k, v]) => `${k} ${v}`)
    .join(" · ");
}

/** The language, in the space under the box. */
function Reference({ simulator }: { simulator: SimulatorSide }) {
  return (
    <details className="text-muted-foreground text-xs">
      <summary className="cursor-pointer select-none">How the rules work</summary>
      <div className="mt-2 grid gap-1.5 leading-5">
        <p>
          <code className="font-mono">goal: distance &lt;= 0.2 m</code> comes first: what arriving means.
          A run that fails it scores nothing, whatever else it did, and is not told by how much.
          {simulator === "server" ? (
            <>
              {" "}
              Add <code className="font-mono">and heading &lt;= 30</code> to make facing the target part
              of arriving — view 0 is the way to face.
            </>
          ) : null}
        </p>
        <p>
          Then blocks of <code className="font-mono">condition -&gt; points</code>, separated by blank
          lines. Within a block the first matching rule pays; the blocks add up. A{" "}
          <code className="font-mono"># comment</code> above a block names it. The first block must be
          about distance — its first rule is Perfect, any other Partial.
        </p>
        <p>
          Variables: <code className="font-mono">distance</code> (m from the target),{" "}
          {simulator === "server" ? (
            <>
              <code className="font-mono">heading</code> (° off the target heading — view 0),{" "}
              <code className="font-mono">spl</code> (success weighted by path length, 0–1; the shortest
              route's steps over the run's, 1 is unbeatable),{" "}
            </>
          ) : null}
          <code className="font-mono">steps</code>.
          {simulator === "client"
            ? " Heading and SPL are unavailable on a client-simulated challenge: its maze has no target heading and no grid."
            : ""}
        </p>
        <p>
          Compare with <code className="font-mono">&lt;= &lt; &gt;= &gt; == !=</code>, combine with{" "}
          <code className="font-mono">and or not</code>. Points can be a formula:{" "}
          <code className="font-mono">-&gt; max(0, 6 - 30 * distance)</code>. The maximum is what an
          oracle run scores.
        </p>
      </div>
    </details>
  );
}
